// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { NotificationData } from '../types/notification';
import { AppIcon } from './AppIcon';
import './NotificationItem.css';

export interface NotificationItemProps {
  notification: NotificationData;
  /** Pre-formatted relative time string (e.g. "5h ago", "Just now") */
  timeLabel: string;
  /** compact = dropdown (with icon, small delete btn); full = page (no icon, border, outlined delete btn) */
  variant?: 'compact' | 'full';
  onClick?: (id: string) => void;
  onDelete?: (id: string) => void;
  className?: string;
}

export function NotificationItem({
  notification,
  timeLabel,
  variant = 'compact',
  onClick,
  onDelete,
  className = '',
}: NotificationItemProps) {
  const { id, title, message, isRead, clickable, icon } = notification;

  const classes = [
    'fin-notif-item',
    `fin-notif-item--${variant}`,
    !isRead && 'fin-notif-item--unread',
    (clickable || onClick) && 'fin-notif-item--clickable',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = () => {
    onClick?.(id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(id);
    }
  };

  return (
    <div
      className={classes}
      onClick={handleClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {variant === 'compact' && icon && (
        <div className="fin-notif-item__icon">
          {icon}
        </div>
      )}

      <div className="fin-notif-item__content">
        <div className="fin-notif-item__header">
          <span className="fin-notif-item__title">{title}</span>
          <span className="fin-notif-item__time">{timeLabel}</span>
        </div>
        <p className="fin-notif-item__message">{message}</p>
      </div>

      {onDelete ? (
        <button
          className="fin-notif-item__delete"
          onClick={handleDelete}
          aria-label="Delete notification"
          type="button"
        >
          <AppIcon name="trash" color={variant === 'full' ? '#2D0A6C' : 'grey'} size="tiny" />
        </button>
      ) : (
        !isRead && <span className="fin-notif-item__dot" />
      )}
    </div>
  );
}
